<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: ParentTreePanel                                       |
| Section: components/Person                                       |
| Purpose: Reusable ancestor tree panel with controls.             |
+------------------------------------------------------------------+
-->
<template>
    <div ref="treeContainer" class="ParentSection_tree_container"
        :class="{
            'ParentSection_tree_container--fullscreen': isTreeFullscreen,
            'ParentSection_tree_container--auto_height': autoHeight
        }"
        :style="containerStyle">
        <div ref="treeControls" class="ParentSection_tree_controls">
            <div class="ParentSection_tree_controls_generations">
                <span class="ParentSection_tree_controls_label">{{ $t('parentSection.generations') }}</span>
                <div class="ParentSection_tree_controls_stepper">
                    <button @click="selectedGenerations = Math.max(minGenerationCount, selectedGenerations - 1)"
                        class="ParentSection_tree_controls_step_btn"
                        :aria-label="$t('parentSection.lessGenerations')">−</button>
                    <span class="ParentSection_tree_controls_value">{{ selectedGenerations }}</span>
                    <button @click="selectedGenerations = Math.min(maxGenerationCount, selectedGenerations + 1)"
                        class="ParentSection_tree_controls_step_btn"
                        :aria-label="$t('parentSection.moreGenerations')">+</button>
                </div>
            </div>
            <div class="ParentSection_tree_controls_actions">
                <button @click="downloadTreeImage" class="ParentSection_tree_controls_action_btn"
                    :disabled="isExportingTree" :aria-label="$t('parentSection.downloadImage')">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                        fill="currentColor">
                        <path d="M5 20h14v-2H5v2zm7-18v10.17l3.59-3.58L17 10l-6 6-6-6 1.41-1.41L10 12.17V2h2z" />
                    </svg>
                    <span>{{ isExportingTree ? $t('parentSection.exporting') : $t('parentSection.downloadImage')
                        }}</span>
                </button>
                <button @click="toggleFullscreen" class="ParentSection_tree_controls_action_btn"
                    :aria-label="isTreeFullscreen ? $t('parentSection.exitFullscreen') : $t('parentSection.fullscreen')">
                    <svg v-if="!isTreeFullscreen" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                        viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h6v2H5v4H3V3zm12 0h6v6h-2V5h-4V3zM3 15h2v4h4v2H3v-6zm16 4h-4v2h6v-6h-2v4z" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                        fill="currentColor">
                        <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
                    </svg>
                    <span>{{ isTreeFullscreen ? $t('parentSection.exitFullscreen') : $t('parentSection.fullscreen')
                        }}</span>
                </button>
            </div>
        </div>
        <component :is="treeComponent" ref="tree" :personId="personId" :selectedGenerations="treeGenerations"
            :fitToContent="shouldFitToContent" :maxFitZoom="treeMaxFitZoom" :cooperativeGestures="cooperativeGestures"
            :familyGroups="familyGroups" :initialRootPerson="initialRootPerson"
            :includeOriginSiblings="includeOriginSiblings"
            @layout-change="handleLayoutChange" />

        <div class="ParentSection_tree_zoom_controls" @mousedown.stop @click.stop>
            <button class="ParentSection_tree_zoom_btn" type="button" @click="zoomOutTree"
                :aria-label="$t('parentSection.zoomOut')">−</button>
            <button class="ParentSection_tree_zoom_btn" type="button" @click="zoomInTree"
                :aria-label="$t('parentSection.zoomIn')">+</button>
            <button class="ParentSection_tree_zoom_btn ParentSection_tree_zoom_btn--reset" type="button"
                @click="resetTreeView" :aria-label="$t('parentSection.resetView')">{{ $t('parentSection.resetView')
                }}</button>
        </div>
    </div>
</template>

<script>
import AncestorTreePersonCards from '@/views/trees/AncestorTreePersonCards.vue';
import DescendantTreePersonCards from '@/views/trees/DescendantTreePersonCards.vue';

export default {
    name: 'ParentTreePanel',
    components: {
        AncestorTreePersonCards,
        DescendantTreePersonCards
    },
    props: {
        personId: {
            type: String,
            required: true
        },
        initialGenerations: {
            type: Number,
            default: 3
        },
        autoHeight: {
            type: Boolean,
            default: false
        },
        autoHeightVerticalPadding: {
            type: Number,
            default: 128
        },
        fitToContent: {
            type: Boolean,
            default: false
        },
        cooperativeGestures: {
            type: Boolean,
            default: true
        },
        treeType: {
            type: String,
            default: 'ancestors',
            validator: value => ['ancestors', 'descendants'].includes(value)
        },
        familyGroups: {
            type: Array,
            default: () => []
        },
        initialRootPerson: {
            type: Object,
            default: null
        },
        includeOriginSiblings: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedGenerations: this.initialGenerations,
            isFullscreen: false,
            isPseudoFullscreen: false,
            savedScrollY: 0,
            treeLayoutMetrics: null,
            frozenAutoPanelHeight: null,
            panelWidth: 0,
            resizeObserver: null,
            isExportingTree: false
        };
    },
    watch: {
        initialGenerations(newValue) {
            this.selectedGenerations = newValue;
        },
        selectedGenerations() {
            this.resetAutoPanelHeight();
        },
        personId() {
            this.resetAutoPanelHeight();
        },
        treeType() {
            this.resetAutoPanelHeight();
        }
    },
    mounted() {
        document.addEventListener('fullscreenchange', this.onFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
        document.addEventListener('MSFullscreenChange', this.onFullscreenChange);

        if (this.autoHeight && window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(entries => {
                const entry = entries[0];
                const nextWidth = entry?.contentRect?.width || this.$refs.treeContainer?.clientWidth || 0;

                if (Math.abs(nextWidth - this.panelWidth) > 1) {
                    this.resetAutoPanelHeight();
                }

                this.panelWidth = nextWidth;
                this.$nextTick(() => {
                    this.$refs.tree?.fitToView?.();
                });
            });
            this.resizeObserver.observe(this.$refs.treeContainer);
        } else if (this.autoHeight) {
            this.panelWidth = this.$refs.treeContainer?.clientWidth || 0;
            window.addEventListener('resize', this.updateAutoHeightWidth);
        }
    },
    beforeUnmount() {
        document.removeEventListener('fullscreenchange', this.onFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
        document.removeEventListener('MSFullscreenChange', this.onFullscreenChange);
        window.removeEventListener('resize', this.updateAutoHeightWidth);
        this.resizeObserver?.disconnect?.();
        this.disablePseudoFullscreen();
    },
    methods: {
        updateAutoHeightWidth() {
            this.panelWidth = this.$refs.treeContainer?.clientWidth || 0;
            this.resetAutoPanelHeight();
            this.$nextTick(() => {
                this.$refs.tree?.fitToView?.();
            });
        },
        resetAutoPanelHeight() {
            if (!this.autoHeight) {
                return;
            }

            this.treeLayoutMetrics = null;
            this.frozenAutoPanelHeight = null;
        },
        handleLayoutChange(metrics) {
            if (!this.autoHeight) {
                return;
            }

            if (this.frozenAutoPanelHeight) {
                return;
            }

            const width = this.$refs.treeContainer?.clientWidth || this.panelWidth || 0;
            this.treeLayoutMetrics = metrics;
            this.panelWidth = width;
            this.frozenAutoPanelHeight = this.calculateAutoPanelHeight(metrics, width);

            this.$nextTick(() => {
                this.$refs.tree?.fitToView?.();
            });
        },
        calculateAutoPanelHeight(metrics, width) {
            if (!metrics?.contentBounds?.height) {
                return null;
            }

            const { contentBounds } = metrics;
            const controlsHeight = this.$refs.treeControls?.offsetHeight || 45;
            const fitPadding = 24;
            const availableWidth = Math.max((width || 0) - fitPadding * 2, 1);
            const widthFitZoom = contentBounds.width
                ? Math.min(availableWidth / contentBounds.width, 1)
                : 1;
            const effectiveFitZoom = this.treeType === 'descendants'
                ? Math.max(widthFitZoom, 0.34)
                : widthFitZoom;
            const preferredHeight = controlsHeight
                + contentBounds.height * effectiveFitZoom
                + this.autoHeightVerticalPadding;
            const isMobile = window.innerWidth <= 768;
            const isDeepDescendantView = this.treeType === 'descendants' && this.selectedGenerations > 1;
            const minHeight = this.treeType === 'descendants'
                ? (isDeepDescendantView
                    ? (isMobile ? 540 : 660)
                    : (isMobile ? 460 : 560))
                : (isMobile ? 380 : 440);
            const maxHeightRatio = this.treeType === 'descendants'
                ? (isDeepDescendantView ? 0.9 : 0.86)
                : 0.78;
            const maxHeight = Math.max(minHeight, window.innerHeight * maxHeightRatio);

            return Math.round(Math.max(minHeight, Math.min(preferredHeight, maxHeight)));
        },
        enterPseudoFullscreen() {
            this.savedScrollY = window.scrollY || window.pageYOffset || 0;
            document.body.classList.add('ParentSection_body_pseudo_fullscreen');
            document.body.style.top = `-${this.savedScrollY}px`;
            this.isPseudoFullscreen = true;

            this.$nextTick(() => {
                this.$refs.tree?.fitToView?.();
            });
        },
        disablePseudoFullscreen() {
            if (!this.isPseudoFullscreen) {
                return;
            }

            document.body.classList.remove('ParentSection_body_pseudo_fullscreen');
            document.body.style.top = '';
            window.scrollTo(0, this.savedScrollY || 0);
            this.savedScrollY = 0;
            this.isPseudoFullscreen = false;

            this.$nextTick(() => {
                this.$refs.tree?.fitToView?.();
            });
        },
        toggleFullscreen() {
            const container = this.$refs.treeContainer;
            const fullscreenElement =
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement;

            if (this.isPseudoFullscreen) {
                this.disablePseudoFullscreen();
                return;
            }

            if (!fullscreenElement) {
                if (!container) {
                    return;
                }

                const requestFullscreen =
                    container.requestFullscreen ||
                    container.webkitRequestFullscreen ||
                    container.msRequestFullscreen;

                if (requestFullscreen) {
                    const result = requestFullscreen.call(container);

                    if (result && typeof result.catch === 'function') {
                        result.catch(error => {
                            console.warn('Could not enter fullscreen mode:', error);
                            this.enterPseudoFullscreen();
                        });
                    }

                    return;
                }

                this.enterPseudoFullscreen();
                return;
            }

            const exitFullscreen =
                document.exitFullscreen ||
                document.webkitExitFullscreen ||
                document.msExitFullscreen;

            if (exitFullscreen) {
                const result = exitFullscreen.call(document);

                if (result && typeof result.catch === 'function') {
                    result.catch(error => {
                        console.warn('Could not exit fullscreen mode:', error);
                    });
                }
            }
        },
        onFullscreenChange() {
            this.isFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            );

            if (this.isFullscreen) {
                this.disablePseudoFullscreen();
            }

            this.$nextTick(() => {
                this.$refs.tree?.fitToView?.();
            });
        },
        zoomInTree() {
            this.$refs.tree?.zoomIn?.();
        },
        zoomOutTree() {
            this.$refs.tree?.zoomOut?.();
        },
        resetTreeView() {
            this.$refs.tree?.resetView?.();
        },
        async downloadTreeImage() {
            if (this.isExportingTree) {
                return;
            }

            this.isExportingTree = true;

            try {
                await this.$refs.tree?.downloadTreeImage?.();
            } finally {
                this.isExportingTree = false;
            }
        },
    },
    computed: {
        treeComponent() {
            return this.treeType === 'descendants'
                ? DescendantTreePersonCards
                : AncestorTreePersonCards;
        },
        treeGenerations() {
            return this.treeType === 'descendants'
                ? this.selectedGenerations
                : this.selectedGenerations - 1;
        },
        minGenerationCount() {
            return this.treeType === 'descendants' ? 1 : 2;
        },
        maxGenerationCount() {
            return 7;
        },
        shouldFitToContent() {
            return this.autoHeight || this.fitToContent;
        },
        treeMaxFitZoom() {
            if (!this.shouldFitToContent) {
                return 2.4;
            }

            return this.treeType === 'descendants' ? 2.4 : 1;
        },
        isTreeFullscreen() {
            return this.isFullscreen || this.isPseudoFullscreen;
        },
        autoPanelHeight() {
            if (!this.autoHeight || this.isTreeFullscreen || !this.frozenAutoPanelHeight) {
                return null;
            }

            return this.frozenAutoPanelHeight;
        },
        containerStyle() {
            if (!this.autoPanelHeight) {
                return {};
            }

            return {
                '--parent-tree-panel-auto-height': `${this.autoPanelHeight}px`
            };
        },
    }
};
</script>

<style scoped>
/* ── Ancestor tree card canvas ── */
.ParentSection_tree_container {
    display: flex;
    flex-direction: column;
    height: var(--viewport-panel-height-desktop);
    border-radius: 14px;
    overflow: hidden;
    position: relative;
    width: calc(100vw - 40px);
    margin-left: calc(50% - 50vw + 20px);
    margin-right: calc(50% - 50vw + 20px);
}

.ParentSection_tree_container--auto_height {
    height: var(--parent-tree-panel-auto-height, min(640px, var(--viewport-panel-height-desktop)));
    transition: height 0.22s ease;
}

.ParentSection_tree_container--fullscreen,
.ParentSection_tree_container:fullscreen {
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
    margin-top: 0;
    margin-left: 0;
    margin-right: 0;
    width: 100vw;
}

.ParentSection_tree_container--fullscreen {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    z-index: 9999;
}

.ParentSection_tree_container :deep(.ancestor-person-cards-view) {
    min-height: unset;
    flex: 1;
    height: 0;
    padding: 0;
    border-radius: 0;
}

.ParentSection_tree_container :deep(.ancestor-person-cards-view__canvas) {
    min-height: unset;
    height: 100%;
    border-radius: 0;
}

.ParentSection_tree_controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.45);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
    gap: 12px;
}

.ParentSection_tree_controls_generations {
    display: flex;
    align-items: center;
    gap: 10px;
}

.ParentSection_tree_controls_label {
    font-size: 13px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    letter-spacing: 0.02em;
}

.ParentSection_tree_controls_stepper {
    display: flex;
    align-items: center;
    gap: 4px;
}

.ParentSection_tree_controls_step_btn {
    width: 28px;
    height: 28px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
}

.ParentSection_tree_controls_step_btn:hover {
    background: rgba(255, 255, 255, 0.22);
}

.ParentSection_tree_controls_value {
    width: 28px;
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
}

.ParentSection_tree_controls_actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.ParentSection_tree_controls_action_btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: var(--border-radius-md);
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    white-space: nowrap;
}

.ParentSection_tree_controls_action_btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.22);
}

.ParentSection_tree_controls_action_btn:disabled {
    opacity: 0.7;
    cursor: progress;
}

.ParentSection_tree_zoom_controls {
    position: absolute;
    right: 10px;
    bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.45);
    border: 1px solid rgba(255, 255, 255, 0.2);
    z-index: 4;
}

.ParentSection_tree_zoom_btn {
    width: 30px;
    height: 30px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: var(--border-radius-md);
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
}

.ParentSection_tree_zoom_btn:hover {
    background: rgba(255, 255, 255, 0.22);
}

.ParentSection_tree_zoom_btn--reset {
    width: auto;
    padding: 0 10px;
    font-size: 12px;
}

@media (max-width: 768px) {
    .ParentSection_tree_controls {
        background: rgba(0, 0, 0, 0.72);
    }

    .ParentSection_tree_zoom_controls {
        max-width: calc(100% - 20px);
    }
}

:global(body.ParentSection_body_pseudo_fullscreen) {
    position: fixed;
    overflow: hidden;
    inset: 0;
    width: 100%;
}
</style>
